/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Sep 12, 2026 by hoarfroster
 *
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import I18N from '@utils/i18n.base';
import { Button } from '@components/ui/button';
import { acquireScrollLock, releaseScrollLock } from '@components/ui/scroll-lock';
import { trapTabKey } from '@components/ui/focus';
import { isTopMostModalLayer } from '@components/ui/modal-layer';

export interface ConfirmOptions {
    /** Body copy; keep it one sentence stating the consequence. */
    message: ReactNode;
    /** Heading above the message. Falls back to a localized default. */
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Renders the confirm action in the destructive (danger) style. */
    danger?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
    id: number;
    resolve: (value: boolean) => void;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(async () => {
    console.warn('useConfirm called before ConfirmProvider is mounted');
    return false;
});

/** Promise-based confirmation dialog. Replaces window.confirm across the app. */
export function useConfirm(): ConfirmFn {
    return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [queue, setQueue] = useState<PendingConfirm[]>([]);
    const nextId = useRef(1);

    const confirm = useCallback<ConfirmFn>((options) => {
        return new Promise<boolean>((resolve) => {
            setQueue((current) => [...current, { ...options, id: nextId.current++, resolve }]);
        });
    }, []);

    const settle = useCallback((value: boolean) => {
        setQueue((current) => {
            if (current.length === 0) return current;
            const [head, ...rest] = current;
            head.resolve(value);
            return rest;
        });
    }, []);

    const api = useMemo(() => confirm, [confirm]);

    return (
        <ConfirmContext.Provider value={api}>
            {children}
            {queue.length > 0 && <ConfirmDialog options={queue[0]} onSettle={settle} />}
        </ConfirmContext.Provider>
    );
}

function ConfirmDialog({ options, onSettle }: { options: PendingConfirm; onSettle: (value: boolean) => void }) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        acquireScrollLock();
        return () => releaseScrollLock();
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // Confirm always sits above the layer that triggered it; only close when top-most.
                if (!isTopMostModalLayer(panelRef.current)) return;
                event.stopPropagation();
                onSettle(false);
            } else if (event.key === 'Enter' && !(event.target instanceof HTMLTextAreaElement)) {
                // Another layer on top (e.g. an editor above the confirm) owns Enter.
                if (!isTopMostModalLayer(panelRef.current) && !panelRef.current?.contains(event.target as Node)) return;
                event.preventDefault();
                onSettle(true);
            } else if (panelRef.current) {
                trapTabKey(event, panelRef.current);
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [onSettle]);

    // Focus the safe action first: cancel for destructive prompts, confirm otherwise.
    useEffect(() => {
        const container = panelRef.current;
        if (!container) return;
        const target = options.danger
            ? container.querySelector<HTMLButtonElement>('[data-confirm-cancel]')
            : container.querySelector<HTMLButtonElement>('[data-confirm-accept]');
        target?.focus();
    }, [options.danger]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <>
            {/* Backdrop sits above dialog panels (z-50) but below this panel (z-60). */}
            <div className="bg-theme-text/30 fixed inset-0 z-50" onClick={() => onSettle(false)} />
            <div
                ref={panelRef}
                role="alertdialog"
                aria-modal="true"
                aria-label={options.title ?? I18N.app.confirmTitle}
                data-modal-layer="true"
                className="bg-theme-popup-bg shadow-e4 fixed top-1/2 left-1/2 z-60 flex w-full max-w-md -translate-1/2 flex-col rounded-xl"
            >
                <div className="px-4 pt-4 pb-2">
                    <h2 className="text-theme-text m-0 text-base font-semibold">
                        {options.title ?? I18N.app.confirmTitle}
                    </h2>
                    <div className="text-theme-text-light mt-2 text-sm leading-5">{options.message}</div>
                </div>
                <div className="flex justify-end gap-2 px-4 pt-2 pb-4">
                    <Button variant="secondary" data-confirm-cancel onClick={() => onSettle(false)}>
                        {options.cancelLabel ?? I18N.app.cancel}
                    </Button>
                    <Button
                        variant={options.danger ? 'danger' : 'primary'}
                        data-confirm-accept
                        onClick={() => onSettle(true)}
                    >
                        {options.confirmLabel ?? (options.danger ? I18N.app.delete : I18N.app.confirm)}
                    </Button>
                </div>
            </div>
        </>,
        document.body
    );
}
